import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

// eslint-disable-next-line import/no-named-as-default
import EntityDropDownFilterContainer from 'components/entityViewer/entitiesDropDownFilter/EntityDropDownFilterContainer';
import { ApplySubstitutionsConfirmDialog } from 'components/shipping/components/viewBatches/ApplySubstitutionsConfirmDialog';
import { BatchesTable } from 'components/shipping/components/viewBatches/BatchesTable';
import {
    BatchFilterConfigArray,
    batchFiltersToApi,
} from 'components/shipping/components/viewBatches/BatchFilterConfig';
import {
    useApplySubstitutions,
    useBatchesSearch,
    useDownloadBatchFile,
    useUpdateSubstitutionsForBatches,
} from 'components/shipping/components/viewBatches/queries/BatchQueries';
import { UserContext } from 'contexts/UserContext';

export function ViewBatches() {
    const { user } = useContext(UserContext);
    const currentFacilityId = user?.facilityID;

    const [appliedFilterValues, setAppliedFilterValues] = useState({});
    const [downloadingBatchId, setDownloadingBatchId] = useState(null);
    const [applySubstitutionsBatchId, setApplySubstitutionsBatchId] = useState(null);
    const [selectedBatchIds, setSelectedBatchIds] = useState([]);
    const [updateSubstitutionsBatchIds, setUpdateSubstitutionsBatchIds] = useState([]);

    const apiFilters = useMemo(() => batchFiltersToApi(appliedFilterValues), [appliedFilterValues]);

    const { data, error, isFetching, isLoading } = useBatchesSearch({
        facilityId: currentFacilityId,
        filters: apiFilters,
    });
    const rows = useMemo(() => data ?? [], [data]);

    useEffect(() => {
        setSelectedBatchIds([]);
        setUpdateSubstitutionsBatchIds([]);
    }, [apiFilters, currentFacilityId]);

    useEffect(() => {
        const visibleBatchIds = new Set(rows.map((row) => row.id));
        const retainVisibleBatchIds = (batchIds) => {
            const visibleSelection = batchIds.filter((batchId) => visibleBatchIds.has(batchId));
            return visibleSelection.length === batchIds.length ? batchIds : visibleSelection;
        };

        setSelectedBatchIds(retainVisibleBatchIds);
        setUpdateSubstitutionsBatchIds(retainVisibleBatchIds);
    }, [rows]);

    const downloadBatchFileMutation = useDownloadBatchFile({
        onError: (err) => {
            toast.error(err?.message ?? 'Failed to download batch file');
        },
        onSettled: () => {
            setDownloadingBatchId(null);
        },
    });

    const applySubstitutionsMutation = useApplySubstitutions({
        onSuccess: () => {
            toast.success('Additional substitutions queued for this batch');
        },
        onError: (err) => {
            toast.error(err?.message ?? 'Failed to apply substitutions');
        },
    });

    const updateSubstitutionsForBatchesMutation = useUpdateSubstitutionsForBatches({
        onSuccess: (_data, batchIds) => {
            toast.success(`Substitutions queued for ${batchIds.length} ${batchIds.length === 1 ? 'batch' : 'batches'}`);
            setSelectedBatchIds([]);
        },
        onError: (err) => {
            toast.error(err?.message ?? 'Failed to apply substitutions');
        },
    });

    const handleDownloadFile = useCallback(
        (batchId) => {
            if (batchId === undefined || batchId === null || batchId === '') return;
            if (downloadBatchFileMutation.isPending) return;
            setDownloadingBatchId(batchId);
            downloadBatchFileMutation.mutate(batchId);
        },
        [downloadBatchFileMutation],
    );

    const handleApplySubstitutionsRequest = useCallback((batchId) => {
        if (batchId === undefined || batchId === null || batchId === '') return;
        setApplySubstitutionsBatchId(batchId);
    }, []);

    const handleApplySubstitutionsConfirm = useCallback(() => {
        if (
            applySubstitutionsBatchId === undefined ||
            applySubstitutionsBatchId === null ||
            applySubstitutionsBatchId === ''
        ) {
            return;
        }
        applySubstitutionsMutation.mutate(applySubstitutionsBatchId);
        setApplySubstitutionsBatchId(null);
    }, [applySubstitutionsBatchId, applySubstitutionsMutation]);

    const handleApplySubstitutionsOpenChange = useCallback((open) => {
        if (!open) {
            setApplySubstitutionsBatchId(null);
            setUpdateSubstitutionsBatchIds([]);
        }
    }, []);

    const handleUpdateSelectedSubstitutionsRequest = useCallback(() => {
        if (selectedBatchIds.length === 0) return;
        setUpdateSubstitutionsBatchIds(selectedBatchIds);
    }, [selectedBatchIds]);

    const handleUpdateSelectedSubstitutionsConfirm = useCallback(() => {
        if (updateSubstitutionsBatchIds.length === 0) return;
        updateSubstitutionsForBatchesMutation.mutate(updateSubstitutionsBatchIds);
        setUpdateSubstitutionsBatchIds([]);
    }, [updateSubstitutionsBatchIds, updateSubstitutionsForBatchesMutation]);
    const errorMessage = error?.message ?? '';
    const hasFacility = currentFacilityId !== undefined && currentFacilityId !== null && currentFacilityId !== '';

    return (
        <div className="flex min-h-screen flex-col">
            <div className="px-4 pt-4">
                <h1 className="text-xl font-semibold">View Batches</h1>
            </div>
            <div className="flex flex-1 flex-col px-4 pb-6 pt-4">
                <EntityDropDownFilterContainer
                    autoApply={false}
                    title="Batch Filters"
                    filterConfigs={BatchFilterConfigArray}
                    initialValues={appliedFilterValues}
                    onChange={setAppliedFilterValues}
                />
                {errorMessage ? (
                    <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
                        {errorMessage}
                    </div>
                ) : null}
                <BatchesTable
                    data={rows}
                    downloadingBatchId={downloadingBatchId}
                    emptyMessage={hasFacility ? 'No batches found.' : 'Select a facility to view batches.'}
                    isFetching={isFetching}
                    isLoading={isLoading}
                    isUpdatingSelectedSubstitutions={updateSubstitutionsForBatchesMutation.isPending}
                    onApplySubstitutions={handleApplySubstitutionsRequest}
                    onUpdateSelectedSubstitutions={handleUpdateSelectedSubstitutionsRequest}
                    onDownloadFile={handleDownloadFile}
                    onSelectedBatchIdsChange={setSelectedBatchIds}
                    selectedBatchIds={selectedBatchIds}
                />
            </div>
            <ApplySubstitutionsConfirmDialog
                open={applySubstitutionsBatchId !== null}
                onConfirm={handleApplySubstitutionsConfirm}
                onOpenChange={handleApplySubstitutionsOpenChange}
            />
            <ApplySubstitutionsConfirmDialog
                batchCount={updateSubstitutionsBatchIds.length}
                open={updateSubstitutionsBatchIds.length > 0}
                onConfirm={handleUpdateSelectedSubstitutionsConfirm}
                onOpenChange={handleApplySubstitutionsOpenChange}
            />
        </div>
    );
}

ViewBatches.displayName = 'ViewBatches';
